import React from "react";
import { Typography } from "common/components/Typography";
import { IconBadge } from "common/components/map/IconBadge";
import LandslideOutlinedIcon from "@mui/icons-material/LandslideOutlined";
import colors from "common/styles/colors";
import {
  ClimateRiskType,
  PropertyErosionRisk,
} from "../../types/appraiser.types";
import { ClimateRiskTypesTranslations } from "../../helpers/comparableFields.helpers";
import {
  determineBackgroundColor,
  ErosionScenarios,
  formatErosionIndex,
  getErosionAreaName,
  getErosionScenarioSpreadDescription,
} from "../../helpers/climateRisk.helpers";
import { PropertyClimateRiskWrapper } from "./PropertyClimateRiskWrapper";

interface PropertyErosionRiskContentProps {
  erosionRisk?: PropertyErosionRisk;
}

export const PropertyErosionRiskContent: React.FC<
  PropertyErosionRiskContentProps
> = ({ erosionRisk }) => {
  if (!erosionRisk) return null;

  const riskLevel =
    erosionRisk.erosionRisk &&
    Object.values(ClimateRiskType).includes(erosionRisk.erosionRisk)
      ? erosionRisk.erosionRisk
      : undefined;

  const areaName = getErosionAreaName(erosionRisk);
  const erosionScores = erosionRisk.erosionScores;
  const spreadDescription =
    erosionScores &&
    getErosionScenarioSpreadDescription(erosionScores, riskLevel);

  return (
    <div
      style={{
        marginTop: "25px",
        display: "flex",
        justifyContent: "space-between",
      }}
    >
      <PropertyClimateRiskWrapper
        title={<Typography variant="h5">Erozijos rizika</Typography>}
        description={
          <Typography variant="caption">
            Modeliuota pakrančių erozijos rizika pagal kadastro vietoves.
            Lietuvos nacionalinis duomenų rinkinys, eksportuotas 2023 m.
            birželį, trys IPCC RCP klimato scenarijai.
          </Typography>
        }
        icon={
          <IconBadge
            width={40}
            height={40}
            backgroundColor={
              riskLevel ? determineBackgroundColor(riskLevel) : colors.grey
            }
          >
            <LandslideOutlinedIcon
              fontSize="medium"
              style={{ color: "white" }}
            />
          </IconBadge>
        }
        riskType={
          <Typography variant="h4">
            {riskLevel
              ? ClimateRiskTypesTranslations[riskLevel]
              : "Duomenų nėra"}
          </Typography>
        }
        riskDescription={
          <div
            style={{ display: "flex", flexDirection: "column", gap: "8px" }}
          >
            {!!areaName && (
              <Typography variant="caption">
                Kadastro vietovė: {areaName}.
              </Typography>
            )}

            {erosionScores ? (
              <>
                <Typography variant="caption">
                  Santykinis erozijos rodiklis pagal klimato scenarijus:
                </Typography>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "2px",
                  }}
                >
                  {ErosionScenarios.map(({ key, label, gloss }) => (
                    <div
                      key={key}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "10px",
                      }}
                    >
                      <Typography variant="caption">
                        {label} — {gloss}
                      </Typography>
                      <Typography variant="caption">
                        {formatErosionIndex(erosionScores[key])}
                      </Typography>
                    </div>
                  ))}
                </div>
                <Typography variant="caption">
                  Rizikos lygis nustatomas pagal pesimistinį RCP 8.5 scenarijų.
                </Typography>
                {!!spreadDescription && (
                  <Typography variant="caption">
                    {spreadDescription}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="caption">
                Santykinio erozijos rodiklio pagal klimato scenarijus duomenų
                nėra.
              </Typography>
            )}

            <Typography variant="caption">
              Rodiklis apibūdina visą kadastro vietovę, ne konkretų sklypą.
            </Typography>
          </div>
        }
      />
    </div>
  );
};
